import { AccountInfo, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { useVariable } from '@softwareimaging/backstage';
import { FC, PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { create } from 'zustand';
import { NotAuthenticated } from '../../pages/authentication/NotAuthenticated';
import { ContractStatus } from '../api/contracts/partners';
import { useCompany } from '../api/hooks/companies';
import { usePartner } from '../api/hooks/partners';
import { Variables } from '../backstage/config';
import { useAppInsights } from '../insights/useAppInsights';
import { RoleType, useCompanyId, usePartnerId, useRole } from './hooks/user';
import {
  AuthenticationContext,
  AuthenticationInformation,
  AuthenticationStatus,
  AuthenticationStore,
  useAuthentication
} from './useAuthProvider';

const getActivePolicy = (account: AccountInfo): string => {
  const userFlow = account.idTokenClaims?.tfp as string | undefined;
  const customPolicy = account.idTokenClaims?.acr as string | undefined;
  const policy = userFlow ?? customPolicy;
  if (!policy) throw new Error('No user flow found');
  return policy;
};

export const AuthenticationProvider: FC<PropsWithChildren> = ({ children }) => {
  const clientId = useVariable<Variables>('clientId');
  const signInPolicy = useVariable<Variables>('signInPolicy');
  const authority = useVariable<Variables>('authority');
  const tenant = useVariable<Variables>('tenant');
  const apiClientId = useVariable<Variables>('apiClientId');
  const apiScope = useVariable<Variables>('apiScope');
  const signUpPolicy = useVariable<Variables>('signUpPolicy');

  if (!clientId || !signInPolicy || !authority) throw new Error('Missing configuration');

  const instance = useMemo(() => {
    return new PublicClientApplication({
      auth: {
        clientId,
        authority: `${authority}/${tenant}/${signInPolicy}`,
        knownAuthorities: [authority],
        redirectUri: `${window.location.origin}/auth/redirect`,
        postLogoutRedirectUri: `${window.location.origin}/auth/signout/redirect`,
        navigateToLoginRequestUrl: false
      },
      cache: {
        cacheLocation: 'localStorage'
      }
    });
  }, [authority, clientId, signInPolicy, tenant]);

  const [addAuthenticatedUserContext, trackEvent] = useAppInsights(store => [
    store.addAuthenticatedUserContext,
    store.trackEvent
  ]);

  const restoreAuthentication = useCallback(
    (instance: PublicClientApplication): AuthenticationInformation => {
      const [account] = instance.getAllAccounts();
      if (account) {
        const { oid } = account.idTokenClaims as { oid: string };
        addAuthenticatedUserContext(oid);

        return {
          status: AuthenticationStatus.Authenticated,
          account: account
        };
      }

      return {
        status: AuthenticationStatus.NotAuthenticated
      };
    },
    [addAuthenticatedUserContext]
  );

  const store = useMemo(
    () =>
      create<AuthenticationStore>((set, get) => ({
        ...restoreAuthentication(instance),
        getRequestToken: async () => {
          const { account, signIn } = get();
          if (!account) throw new Error('No account found');
          const activePolicy = getActivePolicy(account);
          try {
            return await instance.acquireTokenSilent({
              scopes: [`https://${tenant}/${apiClientId}/${apiScope}`],
              authority: `${authority}/${tenant}/${activePolicy}`,
              account
            });
          } catch (e) {
            if (e instanceof InteractionRequiredAuthError) {
              await signIn();
            }
            throw e;
          }
        },
        onRedirect: async () => {
          const response = await instance.handleRedirectPromise();
          if (response) {
            const { oid, tfp } = response.idTokenClaims as { oid: string; tfp: string };
            addAuthenticatedUserContext(oid);

            if (tfp === signUpPolicy) {
              trackEvent('SIGN_UP:COMPLETE');
            }

            set({
              status: AuthenticationStatus.Authenticated,
              account: response.account
            });

            return response.state;
          } else {
            set({ status: AuthenticationStatus.NotAuthenticated });
          }
        },
        signInSilent: async () => {
          set({ status: AuthenticationStatus.Authenticating });
          const { account } = await instance.ssoSilent({
            scopes: ['openid', 'offline_access']
          });
          set({
            status: AuthenticationStatus.Authenticated,
            account
          });
        },
        signIn: async (from?: string) => {
          window.sessionStorage.removeItem('msal.interaction.status');
          set({ status: AuthenticationStatus.Authenticating });
          await instance.loginRedirect({
            scopes: ['openid', 'offline_access'],
            state: from
          });
        },
        signOut: async () => {
          window.sessionStorage.removeItem('msal.interaction.status');
          set({ status: AuthenticationStatus.NotAuthenticated, account: null });
          await instance.logoutRedirect();
        },
        signUp: async (from?: string) => {
          window.sessionStorage.removeItem('msal.interaction.status');
          trackEvent('SIGN_UP:START');
          set({ status: AuthenticationStatus.Authenticating });
          await instance.loginRedirect({
            scopes: ['openid', 'offline_access'],
            authority: `${authority}/${tenant}/${signUpPolicy}`,
            state: from
          });
        }
      })),
    [
      restoreAuthentication,
      instance,
      tenant,
      apiClientId,
      apiScope,
      authority,
      addAuthenticatedUserContext,
      signUpPolicy,
      trackEvent
    ]
  );

  return <AuthenticationContext.Provider value={store}>{children}</AuthenticationContext.Provider>;
};

export const Authenticated: FC = () => {
  const account = useAuthentication(state => state.account);
  return account ? <Outlet /> : <NotAuthenticated />;
};

const CompanyCheck: FC = () => {
  const navigate = useNavigate();
  const companyId = useCompanyId();
  const partnerId = usePartnerId();

  if (!companyId) {
    throw Error('No company ID found');
  }

  const [company] = useCompany(partnerId, companyId);

  useEffect(() => {
    if (company.disabled) {
      navigate('/disabled');
    } else if (company.contractStatus === ContractStatus.Pending) {
      navigate('/companyOnboarding');
    } else if (company.contractStatus === ContractStatus.Applied) {
      navigate(`/companyOnboarding/msp/${partnerId}/customer/${companyId}/ndaCreated`);
    } else if (company.contractStatus === ContractStatus.NdaSigned) {
      navigate(`/companyOnboarding/msp/${partnerId}/customer/${companyId}/contractSigned`);
    }
  }, [company]);

  /*
   * PRETTY SURE THIS ISN'T USED ANYMORE- CERTAINLY IT'S NOT TESTED
   */
  /*const [response] = useContractApproval(partnerId, companyId);
  useEffect(() => {
    if (response.needsApproval) {
      navigate('/companyContractChangeApproval');
    }
  }, [response]);*/

  /*
   * PRETTY SURE THIS ISN'T USED ANYMORE- CERTAINLY IT'S NOT TESTED
   */
  /*const [purchaseOrderApprovalResponse] = usePurchaseOrderApproval(partnerId, companyId);
  useEffect(() => {
    if (purchaseOrderApprovalResponse.needsApproval) {
      navigate('/companyPurchaseOrderChangeApproval');
    }
  }, [purchaseOrderApprovalResponse]);*/

  return <Outlet />;
};

const PartnerCheck: FC = () => {
  const navigate = useNavigate();
  const partnerId = usePartnerId();

  if (!partnerId) {
    throw Error('No partner ID found');
  }

  const [partner] = usePartner(partnerId);

  useEffect(() => {
    if (partner.disabled) {
      navigate('/disabled');
    } else if (partner.contractStatus === ContractStatus.Pending) {
      navigate('/partnerOnboarding');
    } else if (partner.contractStatus === ContractStatus.Applied) {
      navigate(`/partnerOnboarding/msp/${partnerId}/ndaCreated`);
    } else if (partner.contractStatus === ContractStatus.NdaSigned) {
      navigate(`/partnerOnboarding/msp/${partnerId}/contractSigned`);
    }
  }, [navigate, partner]);

  return <Outlet />;
};

export const Onboarded: FC = () => {
  const role = useRole();

  switch (role.type) {
    case RoleType.Company:
      return <CompanyCheck />;
    case RoleType.Partner:
      return <PartnerCheck />;
    default:
      return <Outlet />;
  }
};
